<!-- ============================================================= -->
<!-- HERO — brain bg-header, "ai automation agency" + dual CTAs    -->
<!-- ============================================================= -->
<section class="aia-hero relative w-full overflow-hidden">
    <div class="aia-hero-bg" aria-hidden="true"></div>
    <div class="aia-hero-fade" aria-hidden="true"></div>

    <div appReveal class="relative z-10 flex flex-col items-center text-center px-4 pt-[260px] sm:pt-[300px] pb-[120px]">
        <span class="aia-eyebrow r-up font-outfit font-bold text-sm tracking-[0.04em] mb-3">semnexus</span>

        <h1 class="aia-hero-title r-up font-outfit font-bold lowercase" style="--reveal-delay: 80ms">
            <span class="gradient-text">ai automation</span> agency
        </h1>

        <div class="r-up mt-8" style="--reveal-delay: 160ms">
            <app-button buttonText="get in touch now!" (clicked)="scheduleCall()"></app-button>
        </div>

        <div class="aia-hero-pitch r-up mt-[70px] max-w-[640px]" style="--reveal-delay: 240ms">
            <p class="font-outfit font-semibold text-lg sm:text-xl mb-5">
                Stop Drowning in Manual Work. Start Scaling with AI.
            </p>
            <p class="font-inter text-sm sm:text-base text-white/75 mb-8">
                SemNexus helps mid-market companies automate the repetitive, time-draining processes
                holding their teams back—so your people can focus on the work that actually moves the
                business forward.
            </p>
            <app-button buttonText="book a discovery call!" (clicked)="scheduleCall()"></app-button>
        </div>
    </div>
</section>

<!-- ============================================================= -->
<!-- THE PROBLEM — split: heading left / two paragraphs right       -->
<!-- ============================================================= -->
<section appReveal class="aia-problem relative w-full px-4 sm:px-[10%] py-[100px] sm:py-[130px]">
    <div class="max-w-[1200px] mx-auto flex flex-col md:flex-row gap-10 md:gap-16 items-start">
        <h2 class="r-left flex-1 font-outfit font-bold lowercase text-3xl sm:text-5xl leading-[1.1]">
            the problem isn't<br>your team.<br>
            <span class="gradient-text">it's the workload.</span>
        </h2>

        <div class="r-right flex-1 font-inter text-sm sm:text-base text-white/75 space-y-6" style="--reveal-delay: 120ms">
            <p>
                Your business is growing, but so is the operational drag. Manual data entry,
                disconnected systems, repetitive customer interactions, and reporting that eats entire
                afternoons. Hiring more people only adds cost and complexity.
            </p>
            <p>
                AI automation changes the equation. Instead of throwing more headcount at the problem,
                you build intelligent systems that handle the routine work—accurately, around the clock,
                and at a fraction of the cost.
            </p>
        </div>
    </div>
</section>

<!-- ============================================================= -->
<!-- SERVICES — pill label (left) + paired description (right)      -->
<!-- ============================================================= -->
<section appReveal class="aia-services relative w-full px-4 sm:px-[10%] py-[90px] sm:py-[120px]">
    <div class="max-w-[1100px] mx-auto">
        <h2 class="r-up text-center font-outfit font-bold lowercase text-3xl sm:text-5xl mb-14 sm:mb-20">
            <span class="gradient-text">ai automation</span> services
        </h2>

        <div class="flex flex-col gap-6 sm:gap-7">
            @for (svc of services; track svc.label; let i = $index) {
            <div class="aia-svc-row r-up flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10"
                [style.--reveal-delay]="i * 90 + 'ms'">
                <div class="aia-svc-pill font-outfit font-semibold text-sm sm:text-base">
                    {{ svc.label }}
                </div>
                <p class="aia-svc-detail flex-1 font-inter text-sm sm:text-base text-white/70">
                    {{ svc.detail }}
                </p>
            </div>
            }
        </div>
    </div>
</section>

<!-- ============================================================= -->
<!-- HOW IT WORKS — cascading numbered glass staircase             -->
<!-- ============================================================= -->
<section appReveal class="aia-how relative w-full px-4 sm:px-[10%] py-[90px] sm:py-[120px]">
    <div class="max-w-[1000px] mx-auto">
        <h2 class="r-up text-center font-outfit font-bold lowercase text-3xl sm:text-5xl mb-14 sm:mb-20">
            how <span class="gradient-text">it works</span>
        </h2>

        <div class="flex flex-col gap-6 sm:gap-7">
            @for (step of steps; track step.term; let i = $index) {
            <div class="aia-step r-rise" [class]="'aia-step-' + i" [style.--reveal-delay]="i * 110 + 'ms'">
                <div appSpotlight class="aia-step-card flex items-start gap-5 sm:gap-7 rounded-[22px] p-6 sm:p-8">
                    <span class="aia-step-num font-outfit font-bold">{{ i + 1 }}</span>
                    <div>
                        <div class="aia-step-term font-outfit font-bold text-lg sm:text-xl mb-2 gradient-text">
                            {{ step.term }}
                        </div>
                        <p class="font-inter text-sm sm:text-base text-white/70">{{ step.detail }}</p>
                    </div>
                </div>
            </div>
            }
        </div>
    </div>
</section>

<!-- ============================================================= -->
<!-- WHY CHOOSE — gradient pills (left) + reasons (right) + CTA     -->
<!-- ============================================================= -->
<section appReveal class="aia-why relative w-full px-4 sm:px-[10%] py-[90px] sm:py-[120px]">
    <div class="max-w-[1100px] mx-auto">
        <h2 class="r-up text-center font-outfit font-bold text-3xl sm:text-5xl leading-[1.15] mb-14 sm:mb-20">
            why mid-market companies<br>choose <span class="gradient-text">semnexus</span>
        </h2>

        <div class="flex flex-col gap-6 sm:gap-7">
            @for (reason of reasons; track reason.label; let i = $index) {
            <div class="aia-reason-row r-up flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10"
                [style.--reveal-delay]="i * 90 + 'ms'">
                <div class="aia-reason-pill flex items-center gap-3 font-outfit font-semibold text-sm sm:text-base">
                    <img src="/images/logo/mark.svg" alt="" class="aia-reason-mark" aria-hidden="true" />
                    <span>{{ reason.label }}</span>
                </div>
                <p class="flex-1 font-inter text-sm sm:text-base text-white/70">{{ reason.detail }}</p>
            </div>
            }
        </div>

        <!-- Closing CTA -->
        <div appReveal class="mt-[90px] sm:mt-[120px] flex flex-col items-center text-center">
            <h3 class="r-up font-outfit font-bold text-2xl sm:text-4xl mb-5 lowercase">
                ready to see <span class="gradient-text">what ai can do for your business</span>?
            </h3>
            <p class="r-up font-inter text-sm sm:text-base text-white/65 max-w-[620px] mb-9"
                style="--reveal-delay: 100ms">
                In a free 30-minute discovery call, we'll identify where automation can save you the most
                time and money—no obligation, no jargon.
            </p>
            <div class="r-up" style="--reveal-delay: 180ms">
                <app-button buttonText="book a discovery call!" (clicked)="scheduleCall()"></app-button>
            </div>
        </div>
    </div>
</section>
